// logger.js — structured logger with levels and verbosity gate (ARD Logger contract).
// Exposes window.Logger = { log, getBuffer, dump, setLevel, enableConsole, traced, tracedAsync }.

window.Logger = (() => {
  const LEVELS = { debug: 0, info: 1, warn: 2, error: 3 };
  const BUFFER_SIZE = 500;
  const _buffer = [];
  let _minLevel = LEVELS.info;
  let _consoleEnabled = false;

  // ---------------------------------------------------------------------------
  // Production console gate
  // ---------------------------------------------------------------------------
  // In production (location.protocol !== 'file:'), console output is disabled
  // by default. It can be re-enabled via Logger.enableConsole(true) or by
  // loading the page with ?debug in the query string.
  (function _initConsoleGate() {
    if (location.protocol === 'file:') {
      _consoleEnabled = true;
    } else if (location.search.indexOf('debug') !== -1) {
      _consoleEnabled = true;
    }
  })();

  // ---------------------------------------------------------------------------
  // Core logging
  // ---------------------------------------------------------------------------

  function log(entry) {
    const level = entry.level || 'info';
    const levelVal = LEVELS[level] !== undefined ? LEVELS[level] : LEVELS.info;

    // Always store (ring buffer) — dump() needs everything for the harness
    if (_buffer.length >= BUFFER_SIZE) {
      _buffer.shift();
    }
    _buffer.push({ ...entry, level });

    // Console gate: skip if below min level or console disabled
    if (levelVal < _minLevel || !_consoleEnabled) return;

    const method = level === 'error' ? console.error
      : level === 'warn' ? console.warn
      : console.log;
    method('[radio]', entry.module, entry.fn, entry);
  }

  function getBuffer() {
    return [..._buffer];
  }

  /** Returns a JSON-serializable snapshot of the full log buffer (for GLM harness). */
  function dump() {
    return _buffer.map(e => {
      // Strip non-serializable fields (Error objects, functions)
      const copy = { ...e };
      if (copy.error instanceof Error) {
        copy.error = { name: copy.error.name, message: copy.error.message, stack: copy.error.stack };
      }
      // Remove circular/function values from inputs/output
      try { JSON.stringify(copy); } catch (_) {
        copy.inputs = '[non-serializable]';
        copy.output = '[non-serializable]';
      }
      return copy;
    });
  }

  function setLevel(level) {
    if (LEVELS[level] !== undefined) {
      _minLevel = LEVELS[level];
    }
  }

  function enableConsole(enabled) {
    _consoleEnabled = !!enabled;
  }

  // ---------------------------------------------------------------------------
  // Tracing wrappers
  // ---------------------------------------------------------------------------

  function traced(module, fn, impl, opts) {
    const level = (opts && opts.level) || 'info';
    return function(...args) {
      const t0 = Date.now();
      try {
        const output = impl.apply(this, args);
        log({ ts: t0, module, fn, inputs: args, output, durationMs: Date.now() - t0, error: null, level });
        return output;
      } catch (e) {
        log({ ts: t0, module, fn, inputs: args, output: null, durationMs: Date.now() - t0, error: e, level: 'error' });
        return { error: e.message };
      }
    };
  }

  function tracedAsync(module, fn, impl, opts) {
    const level = (opts && opts.level) || 'info';
    return async function(...args) {
      const t0 = Date.now();
      try {
        const output = await impl.apply(this, args);
        log({ ts: t0, module, fn, inputs: args, output, durationMs: Date.now() - t0, error: null, level });
        return output;
      } catch (e) {
        log({ ts: t0, module, fn, inputs: args, output: null, durationMs: Date.now() - t0, error: e, level: 'error' });
        return { error: e.message };
      }
    };
  }

  return { log, getBuffer, dump, setLevel, enableConsole, traced, tracedAsync };
})();
